import { FormEvent, useEffect, useState } from 'react';
import { RotateCcw, UsersRound } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type StaffOperationsData = {
  rota: Array<{ id: string; role?: string | null; locationId?: string | null; startsAt: string; endsAt: string; status: string }>;
  timesheets: Array<{ id: string; locationId?: string | null; clockInAt: string; clockOutAt?: string | null; status: string }>;
  commissions: Array<{ id: string; providerId?: string | null; serviceId?: string | null; rateType: string; rateValue: number | string; active: boolean }>;
  payrollExports: Array<{ id: string; rangeStart: string; rangeEnd: string; status: string; exportFormat: string }>;
  productivity: Array<{ providerId: string; providerName: string; appointments: number; completed: number; noShows: number }>;
};

export default function StaffOperationsPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<StaffOperationsData | null>(null);
  const [error, setError] = useState('');
  const [role, setRole] = useState('');
  const [rateValue, setRateValue] = useState('');

  async function load() {
    try {
      setError('');
      setData(await fetchJson<StaffOperationsData>('/staff-operations'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load staff operations.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createShift(event: FormEvent) {
    event.preventDefault();
    const startsAt = new Date();
    startsAt.setHours(9, 0, 0, 0);
    const endsAt = new Date(startsAt);
    endsAt.setHours(17, 0, 0, 0);
    await fetchJson('/staff-operations/rota-shifts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startsAt: startsAt.toISOString(), endsAt: endsAt.toISOString(), role: role || undefined }),
    });
    setRole('');
    await load();
  }

  async function createTimesheet() {
    const clockInAt = new Date();
    await fetchJson('/staff-operations/timesheets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clockInAt: clockInAt.toISOString(), notes: 'Manual staff operations entry' }),
    });
    await load();
  }

  async function createCommission(event: FormEvent) {
    event.preventDefault();
    await fetchJson('/staff-operations/commission-rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rateType: 'PERCENT', rateValue: Number(rateValue || 0) }),
    });
    setRateValue('');
    await load();
  }

  async function createPayrollExport() {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 14);
    await fetchJson('/staff-operations/payroll-exports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rangeStart: start.toISOString(), rangeEnd: end.toISOString(), exportFormat: 'CSV' }),
    });
    await load();
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Staff operations</p>
          <h1 className="text-2xl font-semibold text-slate-900">Rota, timesheets, commissions, and productivity</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><RotateCcw size={16} /> Refresh</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <form onSubmit={createShift} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Add rota shift</div>
          <input value={role} onChange={(event) => setRole(event.target.value)} placeholder="Role or assignment" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Create shift</button>
        </form>
        <form onSubmit={createCommission} className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Add commission rule</div>
          <input value={rateValue} onChange={(event) => setRateValue(event.target.value)} placeholder="Percent rate" type="number" min="0" step="0.01" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Create commission rule</button>
        </form>
        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Payroll actions</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void createTimesheet()} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Clock in</button>
            <button type="button" onClick={() => void createPayrollExport()} className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Create payroll export</button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Staff rota" rows={(data?.rota || []).map((row) => [row.role || row.locationId || 'Shift', `${new Date(row.startsAt).toLocaleString()} - ${row.status}`])} />
        <Panel title="Timesheets" rows={(data?.timesheets || []).map((row) => [row.locationId || 'Timesheet', `${new Date(row.clockInAt).toLocaleString()} - ${row.status}`])} />
        <Panel title="Commissions" rows={(data?.commissions || []).map((row) => [row.providerId || row.serviceId || 'Default rule', `${row.rateType} ${row.rateValue} - ${row.active ? 'Active' : 'Inactive'}`])} />
        <Panel title="Payroll exports" rows={(data?.payrollExports || []).map((row) => [row.exportFormat, `${new Date(row.rangeStart).toLocaleDateString()} to ${new Date(row.rangeEnd).toLocaleDateString()} - ${row.status}`])} />
        <Panel title="Provider productivity" rows={(data?.productivity || []).map((row) => [row.providerName, `${row.completed}/${row.appointments} completed, ${row.noShows} no-shows`])} />
      </div>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><UsersRound size={16} /> {title}</div>
      <div className="mt-3 divide-y divide-slate-100">
        {rows.length ? rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="flex items-center justify-between gap-3 py-2 text-sm">
            <span className="text-slate-700">{label}</span>
            <span className="text-right text-slate-500">{value}</span>
          </div>
        )) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}
      </div>
    </div>
  );
}
